import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';

// ---------------------------------------------------------------------------
// Switch – reimagined toggle switch with Tailwind styling
// ---------------------------------------------------------------------------

export type SwitchSize = 'sm' | 'md' | 'lg';

interface SwitchSignature {
  Args: {
    /** Whether the switch is on */
    checked?: boolean;
    /** Called when the switch state changes */
    onChange?: (checked: boolean) => void;
    /** Label text displayed next to the switch */
    label?: string;
    /** Hint text displayed below the label */
    hint?: string;
    /** Validation error messages */
    errors?: string[];
    /** Disable the switch */
    isDisabled?: boolean;
    /** Size variant @default "md" */
    size?: SwitchSize;
  };
  Element: HTMLButtonElement;
}

// ---------------------------------------------------------------------------
// Size classes
// ---------------------------------------------------------------------------

const SIZE_CLASSES: Record<SwitchSize, { track: string; thumb: string; translate: string }> = {
  sm: { track: 'w-7 h-4', thumb: 'w-3 h-3', translate: 'translate-x-3' },
  md: { track: 'w-9 h-5', thumb: 'w-3.5 h-3.5', translate: 'translate-x-4' },
  lg: { track: 'w-11 h-6', thumb: 'w-4.5 h-4.5', translate: 'translate-x-5' },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Reimagined Switch — a zero-dependency toggle switch component.
 *
 * Renders as a button with role="switch" for accessibility.
 * Uses violet accent when checked, matching other reimagined components.
 *
 * @example Basic usage
 * ```gts
 * import Switch from 'tio-ui/components/switch';
 *
 * <Switch
 *   @label="Enable notifications"
 *   @checked={{this.notificationsEnabled}}
 *   @onChange={{this.setNotifications}}
 * />
 * ```
 *
 * @example With hint and errors
 * ```gts
 * <Switch
 *   @label="Two-factor authentication"
 *   @hint="Adds an extra layer of security to your account"
 *   @checked={{this.twoFaEnabled}}
 *   @onChange={{this.setTwoFa}}
 *   @errors={{this.twoFaErrors}}
 * />
 * ```
 */
export default class Switch extends Component<SwitchSignature> {
  private _fieldId = `reimagined-switch-${guidFor(this)}`;
  private _labelId = `reimagined-switch-label-${guidFor(this)}`;

  private get size(): SwitchSize {
    return this.args.size ?? 'md';
  }

  private get sizeClasses() {
    return SIZE_CLASSES[this.size];
  }

  private get hasErrors(): boolean {
    return Boolean(this.args.errors?.length);
  }

  toggle = () => {
    if (this.args.isDisabled) return;
    this.args.onChange?.(!this.args.checked);
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle();
    }
  };

  <template>
    <div data-test-switch class="flex flex-col gap-1 {{if @isDisabled 'opacity-50'}}" ...attributes>
      <div class="tio-row">
        <button
          id={{this._fieldId}}
          data-test-switch-toggle
          type="button"
          role="switch"
          aria-checked={{if @checked "true" "false"}}
          aria-labelledby={{if @label this._labelId}}
          aria-invalid={{if this.hasErrors "true"}}
          disabled={{@isDisabled}}
          class="relative inline-flex items-center shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out cursor-pointer
            {{this.sizeClasses.track}}
            {{if @checked 'bg-violet-500 dark:bg-violet-500' 'bg-gray-200'}}
            focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/20 focus-visible:ring-offset-2
            {{if @isDisabled 'cursor-not-allowed'}}"
          {{on "click" this.toggle}}
          {{on "keydown" this.handleKeyDown}}
        >
          <span
            aria-hidden="true"
            class="pointer-events-none inline-block rounded-full bg-white dark:bg-gray-100 shadow-sm ring-0 transition-transform duration-200 ease-in-out
              {{this.sizeClasses.thumb}}
              {{if @checked this.sizeClasses.translate 'translate-x-0.5'}}"
          ></span>
        </button>

        {{#if @label}}
          <label
            id={{this._labelId}}
            data-test-switch-label
            for={{this._fieldId}}
            class="text-sm font-medium tio-fg-secondary cursor-pointer select-none
              {{if @isDisabled 'cursor-not-allowed'}}"
          >
            {{@label}}
          </label>
        {{/if}}
      </div>

      {{#if @hint}}
        <p data-test-switch-hint class="text-xs tio-fg-muted ml-12">{{@hint}}</p>
      {{/if}}

      {{#if this.hasErrors}}
        <div data-test-switch-errors class="ml-12 space-y-0.5">
          {{#each @errors as |error|}}
            <p class="text-xs text-red-600 dark:text-red-400" role="alert">{{error}}</p>
          {{/each}}
        </div>
      {{/if}}
    </div>
  </template>
}

export type { SwitchSignature };
